/** Location state key set when navigating from inbox for bag_deleted / collaboration_removed / access denied */
export const BAGS_NOTIFICATION_NOTICE_KEY = 'bagsNotificationNotice';

/** Notice type when user cannot access a bag (private 403 or notification click without access) */
export const BAG_ACCESS_NOTICE_TYPE = 'private_access_denied';

/**
 * @param {{ type: 'bag_deleted' | 'collaboration_removed' | 'private_access_denied', bagName?: string } | undefined } notice
 */
export function getBagsNotificationNoticeMessage(notice) {
  if (!notice?.type) return '';
  const raw = (notice.bagName || '').trim();
  const fallbackLabel = 'this bag';
  if (notice.type === 'bag_deleted') {
    if (raw) {
      return `“${raw}” was deleted. You no longer have access to it.`;
    }
    return 'That bag was deleted. You no longer have access to it.';
  }
  if (notice.type === 'private_access_denied') {
    if (raw) {
      return `You do not have access to “${raw}”.`;
    }
    return 'You do not have access to this bag.';
  }
  const label = raw || fallbackLabel;
  return `You no longer have access to ${label}.`;
}

/**
 * True when the API result indicates the user cannot view a private bag.
 * @param {{ success?: boolean, error?: string, code?: string, status?: number } | null | undefined} result
 */
export function isPrivateBagAccessDenied(result) {
  if (!result || result.success) return false;
  if (result.code === 'bag_archived') return false;
  const msg = (result.error || '').toLowerCase();
  if (msg.includes('private') && (msg.includes('unauthorized') || msg.includes('forbidden'))) return true;
  if (result.status === 403 && msg.includes('private')) return true;
  return false;
}

/**
 * Unwrap bag payload from getBagById success result (handles nested API shape).
 * @param {{ success?: boolean, data?: object } | null | undefined} bagResult
 */
export function unwrapBagFromGetBagResult(bagResult) {
  if (!bagResult?.success || !bagResult.data) return null;
  const d = bagResult.data;
  return d && d.data ? d.data : d;
}

/**
 * @param {object} notification — inbox row from API
 * @returns {{ pathname: string, search: string, state?: object }}
 */
export function getNotificationNavigationTarget(notification) {
  const m = notification.metadata || {};
  const bagId = m.bag_id;
  const itemId = m.item_id;
  const typesWithItem = new Set([
    'item_added',
    'item_updated',
    'item_deleted',
    'item_assigned',
    'item_unassigned',
  ]);

  if (notification.type === 'bag_deleted' || notification.type === 'collaboration_removed') {
    return {
      pathname: '/bags',
      search: '',
      state: {
        [BAGS_NOTIFICATION_NOTICE_KEY]: {
          type: notification.type,
          bagName: m.bag_name,
        },
      },
    };
  }

  if (bagId) {
    const params = new URLSearchParams();
    if (itemId && typesWithItem.has(notification.type)) {
      params.set('item', itemId);
    }
    const qs = params.toString();
    return {
      pathname: `/bags/${bagId}/items`,
      search: qs ? `?${qs}` : '',
    };
  }

  return {
    pathname: '/bags',
    search: '',
  };
}

/**
 * Resolves navigation for a notification: verifies bag access so removed collaborators
 * (including on public bags) are sent to /bags with a notice instead of a viewer/copy UI.
 * @param {object} notification
 * @param {{ getBagById: (bagId: string, includeAuth?: boolean) => Promise<object> }} bagService
 * @param {string | undefined} userId — authenticated user id; if omitted, sync target is used
 * @returns {Promise<{ pathname: string, search: string, state?: object }>}
 */
export async function resolveNotificationNavigationTargetAsync(notification, bagService, userId) {
  const target = getNotificationNavigationTarget(notification);
  if (target.pathname === '/bags' && target.state?.[BAGS_NOTIFICATION_NOTICE_KEY]) {
    return target;
  }

  const match = target.pathname.match(/^\/bags\/([^/]+)\/items$/);
  if (!match) {
    return target;
  }

  const bagId = match[1];
  const m = notification.metadata || {};

  const bagResult = await bagService.getBagById(bagId, true);

  if (bagResult.success && bagResult.data) {
    const bag = unwrapBagFromGetBagResult(bagResult);
    if (!bag) {
      return target;
    }

    const isOwner = userId && bag.user_id && String(bag.user_id) === String(userId);
    const isCollaborator = !!bag.is_collaborator;

    if (userId && !isOwner && !isCollaborator) {
      // No access: private bags return 403 above; here we handle public bags for removed collaborators
      return {
        pathname: '/bags',
        search: '',
        state: {
          [BAGS_NOTIFICATION_NOTICE_KEY]: {
            type: BAG_ACCESS_NOTICE_TYPE,
            bagName: m.bag_name ?? bag.name,
          },
        },
      };
    }

    return target;
  }

  if (isPrivateBagAccessDenied(bagResult)) {
    return {
      pathname: '/bags',
      search: '',
      state: {
        [BAGS_NOTIFICATION_NOTICE_KEY]: {
          type: BAG_ACCESS_NOTICE_TYPE,
          bagName: m.bag_name,
        },
      },
    };
  }

  return target;
}
